<template>
  <card-dialog
    v-model="modalVisible"
    max-width="600px"
    :title="parentForm.id ? $t('edit_parent.dialog.header') : $t('add_new.parent')"
  >
    <v-card-text
      class="pa-6"
    >
      <validation-observer ref="validation">
        <subtitle>
          {{ $t('profile.title') }}
        </subtitle>

        <v-row no-gutters>
          <v-col class="pr-2">
            <validation-provider
              v-slot="{ errors }"
              rules="required"
            >
              <v-text-field
                v-model="parentForm.first_name"
                :error-messages="errors"
                :label="$t('text_input.data.first_name.title')"
                outlined
                dense
              />
            </validation-provider>
          </v-col>
          <v-col class="pl-2">
            <validation-provider
              v-slot="{ errors }"
              rules="required"
            >
              <v-text-field
                v-model="parentForm.last_name"
                :error-messages="errors"
                :label="$t('text_input.data.last_name.title')"
                outlined
                dense
              />
            </validation-provider>
          </v-col>
        </v-row>

        <validation-provider
          v-slot="{ errors }"
          :rules="{ required: true, unique_username: { user_id: parentForm.user_id || undefined }}"
        >
          <v-text-field
            v-model="parentForm.username"
            :error-messages="errors"
            :label="$t('text_input.user.username.title')"
            :hint="$t('parent_account_username.hint')"
            outlined
            dense
          />
        </validation-provider>

        <validation-provider
          v-slot="{ errors }"
          :rules="{ required: true, email: true, unique_email: { user_id: parentForm.user_id || undefined }}"
        >
          <v-text-field
            v-model="parentForm.email"
            :error-messages="errors"
            :label="$t('text_input.user.email.title')"
            :hint="!parentForm.id ? $t('text_input.staff_email.hint') : undefined"
            outlined
            dense
          />
        </validation-provider>

        <div v-if="!parentForm.id">
          <v-switch
            v-model="manualPassword"
            :label="$t('switch.set_password_manually')"
            persistent-hint
            class="mt-0"
            :hint="manualPassword ? $t('switch.set_password_manually.parent.no_instructions') : $t('switch.set_password_manually.parent.instructions')"
          />

          <div
            v-if="manualPassword"
            class="mt-5"
          >
            <validation-provider
              v-slot="{ errors }"
              rules="required|password_policy"
              vid="password"
            >
              <password-field
                v-model="parentForm.password"
                :error-messages="errors"
                :label="$t('text_input.login.password.title')"
                outlined
                type="password"
                append-icon="fa-light fa-key"
                dense
              />
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
              rules="required|confirmed:password"
              mode="eager"
            >
              <password-field
                v-model="parentForm.password_repeat"
                :error-messages="errors"
                :label="$t('text_input.user.repeat_password.title')"
                outlined
                type="password"
                append-icon="fa-light fa-key"
                dense
              />
            </validation-provider>
          </div>
        </div>

        <subtitle class="mt-5">
          {{ $t('card_title.personal_info') }}
        </subtitle>

        <v-text-field
          v-model="parentForm.address"
          outlined
          dense
          :label="$t('text_input.data.address.title')"
        />

        <v-text-field
          v-model="parentForm.phone"
          outlined
          dense
          :label="$t('text_input.data.phone_number.title')"
          placeholder="+385 99 876 5432"
        />

        <div
          v-if="hasChatModule"
          class="mb-8 mt-2"
        >
          <subtitle class="mb-4">
            {{ $t('edit_parent.chat_settings') }}
          </subtitle>

          <v-row
            align="center"
          >
            <v-col class="body-2">
              {{ $t('switch.messages_to_coach') }}
            </v-col>
            <v-col cols="4">
              <v-switch
                v-model="parentForm.settings.chat_coach_allowed"
                flat
                hide-details
                class="ma-0"
              />
            </v-col>
          </v-row>
        </div>
      </validation-observer>
    </v-card-text>
    <template v-slot:actions>
      <v-card-actions class="pa-4">
        <v-btn
          v-if="parentForm.id"
          class="mt-2"
          color="error"
          text
          small
          @click="deleteConfirmationDialogVisible = true"
        >
          {{ $t('button.delete') }}
        </v-btn>
        <v-spacer />
        <v-btn
          v-if="parentForm.user && !parentForm.user.is_setup_complete"
          outlined
          color="grey darken-2"
          class="text-none"
          :loading="mailResending"
          @click="resendInvitationEmail()"
        >
          {{ $t('button.resend') }}
        </v-btn>
        <v-btn
          color="primary"
          :loading="submitting"
          class="text-none"
          @click="submit()"
        >
          {{ $t('button.save_parent') }}
        </v-btn>
      </v-card-actions>
    </template>

    <delete-confirmation-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      @confirmed="deleteParent()"
    />
  </card-dialog>
</template>

<script>
import CardDialog from '@/components/helpers/CardDialog'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import { ParentApiService } from '@/data/services'
import { EventBus } from '@/plugins/bus'
import { cloneDeep } from 'lodash'
import Subtitle from '@/components/helpers/Subtitle'
import DeleteConfirmationDialog from '@/components/dialogs/DeleteConfirmationDialog'
import { UserApiService } from '@/data/services/UserApiService'
import PasswordField from '@/components/inputs/PasswordField.vue'

const emptySettings = {
  chat_coach_allowed: false
}

const emptyForm = {
  id: null,
  first_name: '',
  last_name: '',
  username: '',
  password: '',
  email: '',
  address: '',
  phone: '',
  settings: emptySettings
}

export default {
  name: 'ParentFormDialog',
  components: { PasswordField, DeleteConfirmationDialog, Subtitle, CardDialog },
  mixins: [deleteConfirmationMixin],
  props: {
    parent: {
      type: Object,
      default: () => ({})
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      parentForm: cloneDeep(emptyForm),
      submitting: false,
      manualPassword: false,
      timeItems: [],
      dayItems: [],
      mailResending: false
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    },
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    hasChatModule() {
      return this.user.organisation_settings.has_chat_module
    }
  },
  created() {
    EventBus.$on(this.$events.OPEN_DIALOG_PARENT_FORM, () => {
      this.parentForm = cloneDeep(emptyForm)
      this.modalVisible = true

      this.$nextTick(() => {
        this.$refs.validation.reset()
      })
    })

    EventBus.$on(this.$events.EDIT_PARENT, (parent) => {
      this.parentForm = { ...this.parentForm, ...cloneDeep(parent) }
      this.modalVisible = true
    })
  },
  methods: {
    async submit() {
      if (!(await this.$refs.validation.validate())) return

      await this.save()
    },
    async save() {
      try {
        this.submitting = true

        const data = {
          id: this.parentForm.id,
          first_name: this.parentForm.first_name,
          last_name: this.parentForm.last_name,
          email: this.parentForm.email,
          username: this.parentForm.username,
          password: this.manualPassword ? this.parentForm.password : undefined,
          address: this.parentForm.address,
          phone: this.parentForm.phone,
          settings: this.parentForm.settings
        }

        let response

        if (this.parentForm.id) {
          response = await ParentApiService.update(data.id, data)
        } else {
          response = await ParentApiService.create(data)
        }

        if (this.parentForm.id) {
          this.$store.dispatch(this.$storeActions.ALERT, {
            text: this.$t('alert.parent_update.success'),
            color: 'success'
          })

          EventBus.$emit(this.$events.PARENT_UPDATED, response.data)
          this.$emit('updated', response.data)
        } else {
          this.$store.dispatch(this.$storeActions.ALERT, {
            text: this.$t('alert.parent_create.success'),
            color: 'success'
          })

          EventBus.$emit(this.$events.PARENT_CREATED, response.data)
          this.$emit('created', response.data)
        }

        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.parent_create.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    },
    async deleteParent() {
      try {
        this.deleting = true

        await ParentApiService.delete(this.parentForm.id)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.parent_delete.success'),
          color: 'success'
        })

        this.modalVisible = false

        EventBus.$emit(this.$events.PARENT_DELETED, this.parentForm)
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.parent_delete.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.deleting = false
      }
    },
    async resendInvitationEmail() {
      if (!this.parentForm.user_id) return

      try {
        this.mailResending = true

        await UserApiService.resendInvitationEmail(this.parentForm.user_id)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.email_send.success'),
          color: 'success'
        })

        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.email_send.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.mailResending = false
      }
    }

  }
}
</script>
